import { WaterScreeningSection } from './WaterResource/WaterScreeningSection';
import React from 'react';
import { walkClaimsText } from '../../../lib/walkClaimsText';
import type { SiteRagReport } from '../../../types/siteRag';
import type { SiteEntity } from '../../../types/site';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';
import { getSectionsForType } from '../../../config/reportSections';
import { hasModernSectionsForReport, resolveEffectiveSectionOrder } from '../../../lib/effectiveSectionOrder';
import { gridLedStandbyPlanningReportHasContent } from '../../../lib/generationImpact/gridLedStandbyPlanningReportContent';
import { GridLedStandbyPlanningSection } from './GridLedStandbyPlanningSection';
import { hiddenReportSectionIds } from '../../../lib/reportGeneration/sectionOmissions';
import { applySectionFallbacks } from '../../../lib/applySectionFallbacks';
import {
  capacityCreditDeepDiveHasContent,
  generationLayoutExhibitHasContent,
  generationScenarioComparisonHasContent,
  waterScreeningHasContent,
} from '../../../lib/tocEntries';
import { ProvenanceStrip } from '../report/ProvenanceStrip';
import { EditableHeading } from '../report/EditableHeading';
import { REPORT_INLINE_SECTION_CHROME_CLASS } from '../../../lib/reportTypography';
import {
  REPORT_SECTION_H2_CLASS,
  REPORT_SECTION_HEADING_TEXT_CLASS,
  reportSectionHeadingStyleScaled,
  reportSectionHeadingClasses,
} from '../../../lib/reportSectionHeading';
import { EditorialSectionShell } from '../report/EditorialSectionShell';
import { resolveFeasibilityStandfirst } from '../../../lib/feasibilityStandfirst';
import { SectionIdProvider, RouterSectionChromeProvider } from './SectionShell';
import { MetaLeakChip } from '../report/MetaLeakChip';
import { resolveEditorialLayer, hasGeneratedEditorialLayer } from '../../../lib/editorial/resolveEditorialLayer';
import { SectionShellContext } from './SectionShell';
import { EditorialProseBody } from '../report/editorial/EditorialProseBody';
// William QA BUG #15 — The Part-divider sheet was restored to fulfill the C5 rubric.
import { computePartDividerBoundaries, resolvePartForSection, EDITORIAL_PARTS, buildPartTocEntries } from '../../../lib/editorial/reportParts';
import { EditorialPartDivider } from '../report/editorial/EditorialPartDivider';
import {
  GridCapacitySection, InterconnectionQueueSection, CurtailmentRiskSection,
  RateStructuresSection, PowerStrategyContextSection, RenewableIntegrationSection,
  GridRedundancySection, BtmTechComparisonSection, LcoeBreakdownSection,
  GridVsBtmSection, PowerRiskMatrixSection, ActionPlanSection,
  WorkloadPowerFitSection,
  // R-FIN-PHASE-D — capacity-credit (ELCC) firm-adequacy + power-basis deep-dive.
  CapacityCreditDeepDiveSection,
  WaterDutySection,
} from './PowerAvailability';
import {
  SupplyAvailabilitySection, GroundwaterSection, WaterRightsSection,
  DroughtResilienceSection, CoolingByTechSection, WastewaterStormwaterSection,
  ConservationSection, TotalWaterCostSection, WaterRiskMatrixSection,
} from './WaterResource';
import {
  ParcelOverviewSection, TopographySection, BuildingPlacementSection,
  SetbackComplianceSection, ParkingAccessSection, StormwaterSection,
  UtilityRoutingSection, PhasedBuildoutSection, MaxCapacitySection,
  GenerationLayoutExhibitSection, GenerationScenarioComparisonSection, RiskMatrixSection,
  // RTU-3 — two build-deliverability canonical additions
  // (constructability + cost-range). Lifted into the modern test-fit
  // decomposition so a test-fit report's sectionOrder can include them
  // and the router will mount them alongside the original 10 RTU-1
  // test-fit sections.
  ConstructabilityAssessmentSection, CostRangeEstimateSection, BopEconomicsSection,
} from './TestFit';
import { PathwayComparisonReportSection, useStudioPreview } from './PathwayComparison/PathwayComparisonReportSection';
import {
  FiberRingsSection, LatencyModelSection, CarrierDiversitySection,
  IxProximitySection, SubmarineCableSection, NetworkResilienceSection,
  BandwidthCapacitySection, CapexOpexSection, ConnectivityRiskMatrixSection,
} from './Interconnection';
import {
  NepaScreeningSection, EcologicalAssessmentSection, WaterResourcesSection,
  AirQualityEmissionsSection, FloodNaturalHazardsSection, CulturalHistoricalSection,
  LandUseZoningSection, NoiseVisualImpactSection, MitigationPlanSection,
  PermittingPathwaySection, EnvironmentalRiskMatrixSection,
  // RTU-2 — three NEPA-canonical additions (alternatives, cumulative
  // impacts, public participation). Lifted into the modern decomposition
  // so an environmental report's sectionOrder can include them and the
  // router will mount them alongside the original 11 RTU-1 sections.
  AlternativesAnalysisSection, CumulativeImpactsSection, PublicParticipationPlanSection,
} from './Environmental';
import {
  ExecutiveSection, StakeholderMapSection, PerBtmSentimentSection,
  ThemeDeepDivesSection, TrendAnalysisSection, ComparableSiteBenchmarkSection,
  MitigationPlaybookSection, SentimentAppendixSection,
} from './PublicSentiment';
import {
  ProcurementOverviewSection, CriticalPathProcurementSection, SupplyChainRisksSection,
  SupplierRiskRankingSection, QuoteComparisonsSection, SupplyChainRiskMatrixSection,
  SupplyChainActionPlanSection,
} from './SupplyChainRisk';
import {
  // FIN-2 (wf w7nuyquwd) — themed READ-ONLY Financial Viability renderers over
  // report.financialAnalysis (+ pathwayComparisonAtGen / conclusion). The
  // pathway_comparison section reuses PathwayComparisonReportSection below.
  DecisionBandSection, FinancialSummarySection, CapexOpexBreakdownSection,
  CashflowStreamSection, MonteCarloBandsSection, RiskAdjustedWaccSection,
  SensitivitySection, TaxAndCommercialSection, FinancialActionPlanSection,
} from './Financial';
import {
  // #34 P2 STAGE B — feasibility's formerly inline-only study sections, lifted
  // into the registry so feasibility routes through this router. The RICH
  // feasibility actionPlan uses FeasibilityActionPlanSection (NOT the generic
  // PowerAvailability ActionPlanSection stub) — branched on reportType below
  // because `actionPlan` is a shared id.
  GridStudySection, TransportStudySection, ConnectivityStudySection,
  RiskAssessmentSection, FeasibilityActionPlanSection,
} from './Feasibility';
import { CustomBriefQuestionsSection } from './CustomBrief/CustomBriefQuestionsSection';
import { CustomBriefQuestionSection } from './CustomBrief/CustomBriefQuestionSection';
import { CustomBriefProjectContextSection } from './CustomBrief/CustomBriefProjectContextSection';
import { BtmViabilitySection } from './ExecutiveSummary/BtmViabilitySection';
// The LIVE Go/No-Go Decision Guide — deterministic, LLM-free, recomputed at
// render (no generate step). `execDecisionBand` is a distinct id (NOT the
// financial-only 'decisionBand').
import { ExecutiveDecisionGuideSection } from './ExecutiveSummary/ExecutiveDecisionGuideSection';
import { PathToGoReportSection } from './ExecutiveSummary/PathToGoReportSection';
import { CrossReportSynthesisSection } from './ExecutiveSummary/CrossReportSynthesisSection';
import { CriticalRisksSection } from './ExecutiveSummary/CriticalRisksSection';
import { SourceReportsSection } from './ExecutiveSummary/SourceReportsSection';
import { ExecutiveSummaryActionPlanSection } from './ExecutiveSummary/ExecutiveSummaryActionPlanSection';
import {
  customBriefQuestionHeading,
  resolveCustomBriefQuestionNumber,
  isCustomBriefQuestionSectionId,
  parseCustomBriefQuestionSectionId,
} from '../../../lib/customBriefSections';
import type { ReportTheme } from '../../../types/reportTheme';
import { DEFAULT_THEME } from '../../../types/reportTheme';

type SectionComponent = React.FC<{ report: SiteRagReport; label: string; sectionId?: string }>;

/**
 * #34 P2 STAGE B — feasibility study sections whose inline shell did NOT carry
 * `space-y-4` (the router otherwise appends it). pathway_comparison is NOT in
 * this set (its inline shell DID carry `space-y-4`), so it keeps the canonical
 * router chrome. Keeps the lifted feasibility shells byte-identical to the
 * inline baseline (reportSurfaceParity oracle).
 */
const FEASIBILITY_NO_SPACE_Y_IDS = new Set([
  'gridStudy', 'capacityCreditDeepDive', 'transportStudy',
  'connectivityStudy', 'riskAssessment', 'actionPlan',
]);

/**
 * WAVE 3 — section ids whose OWN component already mounts the flowing
 * EditorialProseBody (and suppresses its labelled prose blocks in tandem). The
 * router must NOT re-mount the prose body for these, or the monograph prose
 * would render TWICE. Every OTHER routed section gets the prose body emitted by
 * the router (the generic monograph path) when a generated layer is present.
 */
const PROSE_BODY_SELF_MOUNT_IDS = new Set(['gridStudy', 'transportStudy']);

/**
 * Per-type new-section registry. The keys are sectionIds from
 * src/config/reportSections.ts (TEST_FIT_SECTIONS, POWER_AVAILABILITY_SECTIONS,
 * WATER_RESOURCE_SECTIONS, INTERCONNECTION_SECTIONS). Universal IDs (verdict,
 * executiveSummary, conclusion) are absent — they have inline renderers in
 * SiteRagReportView.
 */
export const NEW_SECTION_REGISTRY: Record<string, SectionComponent> = {
  gridLedStandbyPlanning: GridLedStandbyPlanningSection,
  waterScreening: WaterScreeningSection,
  // Power
  powerStrategyContext: PowerStrategyContextSection,
  // Cited, catalog-driven workload↔BTM-power fit (feasibility + power).
  workloadPowerFit: WorkloadPowerFitSection,
  gridCapacity: GridCapacitySection,
  // R-FIN-PHASE-D — firm-capacity (ELCC) adequacy + power-basis deep-dive.
  capacityCreditDeepDive: CapacityCreditDeepDiveSection,
  interconnectionQueue: InterconnectionQueueSection,
  curtailmentRisk: CurtailmentRiskSection,
  rateStructures: RateStructuresSection,
  renewableIntegration: RenewableIntegrationSection,
  gridRedundancy: GridRedundancySection,
  waterDuty: WaterDutySection,
  btmTechComparison: BtmTechComparisonSection,
  lcoeBreakdown: LcoeBreakdownSection,
  gridVsBtm: GridVsBtmSection,
  powerRiskMatrix: PowerRiskMatrixSection,
  actionPlan: ActionPlanSection,
  // Water
  supplyAvailability: SupplyAvailabilitySection,
  groundwater: GroundwaterSection,
  waterRights: WaterRightsSection,
  droughtResilience: DroughtResilienceSection,
  coolingByTech: CoolingByTechSection,
  wastewaterStormwater: WastewaterStormwaterSection,
  conservation: ConservationSection,
  totalWaterCost: TotalWaterCostSection,
  waterRiskMatrix: WaterRiskMatrixSection,
  // Test Fit
  parcelOverview: ParcelOverviewSection,
  topography: TopographySection,
  buildingPlacement: BuildingPlacementSection,
  setbackCompliance: SetbackComplianceSection,
  parkingAccess: ParkingAccessSection,
  stormwater: StormwaterSection,
  utilityRouting: UtilityRoutingSection,
  phasedBuildout: PhasedBuildoutSection,
  maxCapacity: MaxCapacitySection,
  generationLayoutExhibit: GenerationLayoutExhibitSection,
  generationScenarioComparison: GenerationScenarioComparisonSection,
  // RTU-3 (row 0.99235) — two build-deliverability canonical additions
  // (constructability + cost-range). Added to TEST_FIT_SECTIONS in
  // src/config/reportSections.ts so new test-fit reports carry the IDs
  // in sectionOrder and the router mounts them between maxCapacity and
  // riskMatrix.
  constructabilityAssessment: ConstructabilityAssessmentSection,
  costRangeEstimate: CostRangeEstimateSection,
  bop_economics: BopEconomicsSection,
  pathway_comparison: PathwayComparisonReportSection,
  riskMatrix: RiskMatrixSection,
  // Interconnection
  fiberRings: FiberRingsSection,
  latencyModel: LatencyModelSection,
  carrierDiversity: CarrierDiversitySection,
  ixProximity: IxProximitySection,
  submarineCable: SubmarineCableSection,
  networkResilience: NetworkResilienceSection,
  bandwidthCapacity: BandwidthCapacitySection,
  capexOpex: CapexOpexSection,
  connectivityRiskMatrix: ConnectivityRiskMatrixSection,
  // Environmental (RTU-1) — modern decomposition of the formerly
  // monolithic EnvironmentalSections.tsx. The legacy renderer is
  // retained as the fallback for archived reports whose sectionOrder
  // lacks the `nepaScreening` sentinel (see TYPE_SENTINELS below).
  nepaScreening: NepaScreeningSection,
  ecologicalAssessment: EcologicalAssessmentSection,
  waterResources: WaterResourcesSection,
  airQualityEmissions: AirQualityEmissionsSection,
  floodNaturalHazards: FloodNaturalHazardsSection,
  culturalHistorical: CulturalHistoricalSection,
  landUseZoning: LandUseZoningSection,
  noiseVisualImpact: NoiseVisualImpactSection,
  mitigationPlan: MitigationPlanSection,
  permittingPathway: PermittingPathwaySection,
  environmentalRiskMatrix: EnvironmentalRiskMatrixSection,
  // RTU-2 (row 0.99235) — three NEPA-canonical additions; not present
  // in archived pre-RTU-2 environmental reports, but added to
  // ENVIRONMENTAL_SECTIONS in src/config/reportSections.ts so new
  // reports carry the IDs in sectionOrder and the router mounts them.
  alternativesAnalysis: AlternativesAnalysisSection,
  cumulativeImpacts: CumulativeImpactsSection,
  publicParticipationPlan: PublicParticipationPlanSection,
  // Public Sentiment (PSR)
  executive: ExecutiveSection,
  stakeholderMap: StakeholderMapSection,
  perBtmSentiment: PerBtmSentimentSection,
  themeDeepDives: ThemeDeepDivesSection,
  trendAnalysis: TrendAnalysisSection,
  comparableSiteBenchmark: ComparableSiteBenchmarkSection,
  mitigationPlaybook: MitigationPlaybookSection,
  // P3 — the sentiment source-grouped evidence inventory under its OWN id, so it
  // no longer hijacks the standard `appendix` (references/methodology) slot.
  sentimentAppendix: SentimentAppendixSection,
  customBriefQuestions: CustomBriefQuestionsSection,
  projectContext: CustomBriefProjectContextSection,
  // Executive Summary — LIVE Go/No-Go Decision Guide (deterministic, no LLM).
  // `execDecisionBand` is the exec-summary analogue of the financial-only
  // `decisionBand`; `pathToGo` renders the live Path-to-GO steps read-only.
  execDecisionBand: ExecutiveDecisionGuideSection,
  pathToGo: PathToGoReportSection,
  btmViability: BtmViabilitySection,
  crossReportSynthesis: CrossReportSynthesisSection,
  criticalRisks: CriticalRisksSection,
  sourceReports: SourceReportsSection,
  // Supply Chain Risk
  procurementOverview: ProcurementOverviewSection,
  criticalPathProcurement: CriticalPathProcurementSection,
  supplyChainRisks: SupplyChainRisksSection,
  supplierRiskRanking: SupplierRiskRankingSection,
  quoteComparisons: QuoteComparisonsSection,
  supplyChainRiskMatrix: SupplyChainRiskMatrixSection,
  // Financial Viability (FIN-2) — themed READ-ONLY renderers over the
  // deterministic `report.financialAnalysis` stamp. `pathway_comparison`
  // reuses PathwayComparisonReportSection (already registered above).
  decisionBand: DecisionBandSection,
  financialSummary: FinancialSummarySection,
  capexOpexBreakdown: CapexOpexBreakdownSection,
  cashflowStream: CashflowStreamSection,
  monteCarloBands: MonteCarloBandsSection,
  riskAdjustedWacc: RiskAdjustedWaccSection,
  sensitivity: SensitivitySection,
  taxAndCommercial: TaxAndCommercialSection,
  financialActionPlan: FinancialActionPlanSection,
  // Feasibility (#34 P2 STAGE B) — the formerly inline-only study sections,
  // lifted so feasibility routes through this router (feasibility TYPE_SENTINEL).
  // `capacityCreditDeepDive` is already registered above (shared with power);
  // `actionPlan` is shared across types and resolves to FeasibilityActionPlanSection
  // via the reportType-keyed override below (NOT the generic stub).
  gridStudy: GridStudySection,
  transportStudy: TransportStudySection,
  connectivityStudy: ConnectivityStudySection,
  riskAssessment: RiskAssessmentSection,
};

/**
 * True if the report is using the new per-type section list. Used to gate
 * legacy aggregate blocks so they don't double-render alongside the new ones.
 * Falls back to the type's canonical sectionOrder when `report.sectionOrder`
 * is absent — matching TOC/body resolution (RTB extension).
 */
export function hasModernSections(report: Pick<SiteRagReport, 'reportType' | 'sectionOrder'>): boolean {
  return hasModernSectionsForReport(report);
}

interface RouterProps {
  report: SiteRagReport;
  /** Function returning the React.CSSProperties for a given sectionId — supplies CSS order/visibility from the parent renderer. */
  sectionStyle: (sectionId: string) => React.CSSProperties;
  /** Resolved page column count (1|2) per section — stamps `data-section-columns`. */
  sectionPageColumnCount?: (sectionId: string) => 1 | 2;
  /**
   * ROUND-3 DEFECT-A — page-level flow classification (island|flow|span) per
   * section, stamps `data-section-flow`. WITHOUT this the router's modern
   * per-type sections (nepaScreening, waterResources, gridCapacity, …) carried
   * NO `data-section-flow`, so the page-flow deck CSS defaulted them to
   * column-span:all (span) — i.e. ZERO flow sections and dead 2-col prose on
   * every non-feasibility report. Returns `undefined` for single-column /
   * legacy presets (stamps nothing → byte-stable).
   */
  sectionFlowMode?: (sectionId: string) => string | undefined;
  /** TOC-aligned § N stamp when section-order flatten disables the CSS counter. */
  sectionNumberProps?: (sectionId: string) => { 'data-section-number'?: string };
  /** When supplied with `sed`, SNT-5 fallbacks hydrate empty typed fields at render time. */
  site?: SiteEntity;
  sed?: SiteEvidenceDocument | null;
  theme?: ReportTheme;
  omitSectionSidebar?: boolean;
  /** Per-section shell padding when page wrappers flatten to `display: contents`. */
  shellPadding?: boolean;
  sectionChrome?: string;
  isEditing?: boolean;
  headingOverrides?: Record<string, string>;
  onHeadingChange?: (key: string, value: string) => void;
  sectionDefaultHeading?: (key: string, fallback: string) => string;
  isAdmin?: boolean;
  onSectionRegen?: (sectionId: string) => Promise<void> | void;
  /**
   * v1.2.2 — suppress the public-sentiment `executive` body section (and let
   * the parent drop its TOC entry) when the cover layout already carries the
   * exec-summary-equivalent (hero / masthead / classic). Mirrors the inline
   * `executiveSummary` de-dup that SiteRagReportView applies for the other
   * report types. Default `false` → byte-stable for every non-sentiment /
   * non-carrying-cover path.
   */
  suppressExecutiveSection?: boolean;
  /**
   * Part L (editorial-layouts-l1) — when true, prepend the editorial OPENER (a
   * big SERIF hero-numeral, EditorialSectionLayout's exact numeral treatment)
   * above each modern section's existing head. The section's <h2> and CONTENT
   * are UNCHANGED — the numeral is the only thing added. The parent
   * (SiteRagReportView) sets this ONLY when `isEditorialLayoutsEnabled()` AND
   * the active preset is `editorial`. Default `false` → nothing is added, so
   * every existing report/preset renders byte-identically (the flag-OFF state).
   */
  editorialActive?: boolean;
}

/**
 * Iterates the report's sectionOrder and renders the matching new-section
 * component for each ID in the registry. Universal IDs (verdict,
 * executiveSummary, conclusion) and any unregistered ID are skipped — they're
 * handled by the inline blocks in SiteRagReportView.
 *
 * Two guarantees this router enforces:
 *
 *   1. **Modern-only fire.** Returns null when the report is not using the
 *      new per-type sectionOrder (feasibility and pre-modern
 *      power/water/test-fit/interconnection/environmental reports). Those
 *      reports already render their content via the inline blocks in
 *      SiteRagReportView / EnvironmentalSections / SpecializedSections.
 *      Without this gate, a sectionId like `actionPlan` would mount BOTH
 *      inline (rich variant) and via the registry (PowerAvailability
 *      stub) on a feasibility report, producing a duplicated heading +
 *      a customer-facing "Insufficient data" diagnostic.
 *
 *   2. **Within-iteration dedupe.** Even with a clean sectionOrder, a
 *      template editor regression (or a hand-edited config) could include
 *      the same id twice. Track seen ids and skip repeats so the rendered
 *      DOM never carries two mounts for the same sectionId.
 */
/**
 * PH-A3 / PH-C1 — wrap in React.memo. The parent (SiteRagReportView)
 * passes a useCallback-stabilised `sectionStyle` and the `report` ref
 * is stable until the underlying report changes, so the shallow-prop
 * comparison now reliably short-circuits re-renders.
 */
export const NewTypeSectionsRouter: React.FC<RouterProps> = React.memo(({
  report,
  sectionStyle,
  sectionPageColumnCount,
  sectionFlowMode,
  sectionNumberProps,
  site,
  sed,
  theme = DEFAULT_THEME,
  omitSectionSidebar = false,
  shellPadding = true,
  sectionChrome = REPORT_INLINE_SECTION_CHROME_CLASS,
  isEditing = false,
  headingOverrides,
  onHeadingChange,
  sectionDefaultHeading,
  isAdmin = false,
  onSectionRegen,
  suppressExecutiveSection = false,
  editorialActive = false,
}) => {
  const routerChrome = React.useMemo(
    () => ({
      isEditing,
      headingOverrides: headingOverrides ?? {},
      onHeadingChange,
      sectionDefaultHeading,
      isAdmin,
      onSectionRegen,
    }),
    [isEditing, headingOverrides, onHeadingChange, sectionDefaultHeading, isAdmin, onSectionRegen],
  );
  const effectiveReport = React.useMemo(() => {
    const type = report.reportType;
    // #34 P2 STAGE B — feasibility now routes through this router (its
    // TYPE_SENTINEL is set), so it must ALSO get the SNT-5 typed-field
    // fallbacks (else the modern feasibility study sections render the bare
    // empty-state instead of the derived fallback when SED + site exist). The
    // historical `type === 'feasibility'` skip dated from when feasibility was
    // inline-only; with feasibility on the router it would leave the modern
    // sections blank. Fallbacks only fire when site + sed are BOTH present, so
    // the SED-less render (fixtures / archived shapes) is byte-stable.
    if (!type || !site || !sed) return report;
    const applied = applySectionFallbacks(
      type,
      report as unknown as Record<string, unknown>,
      site,
      sed,
    );
    if (applied.appliedCount === 0) return report;
    return applied.data as unknown as SiteRagReport;
  }, [report, site, sed]);
  // HR-QA fix (2026-06-10) — pathway_comparison bare-heading guard. The
  // router shell ALWAYS renders the section <h2>, but
  // PathwayComparisonReportSection self-omits (returns null) when no pinned
  // comparison with 2+ alternatives exists — so test-fit / financial PDFs
  // shipped a ruled "Pathway Comparison" heading with NO body. Mirror the
  // feasibility inline mount (SiteRagReportView "Task 3" gate): skip the
  // WHOLE shell on the same condition, keeping the Studio preview placeholder
  // (studioPreview keeps the section visible for template authors).
  const studioPreview = useStudioPreview();

  if (!hasModernSections(effectiveReport)) return null;
  const reportType = effectiveReport.reportType;
  if (!reportType) return null;
  const sectionsForType = getSectionsForType(reportType, effectiveReport);
  const persistedOrder = gridLedStandbyPlanningReportHasContent(effectiveReport)
    ? resolveEffectiveSectionOrder({ reportType, sectionOrder: effectiveReport.sectionOrder, report: effectiveReport })
    : effectiveReport.sectionOrder ?? sectionsForType.map(s => s.sectionId);
  // Drop sections the generator recorded as unevidenced. `qaFindings
  // .sectionOmissions` is stamped at generation time and means something
  // stronger than "empty": the report's evidence domain provably has nothing,
  // so no regeneration can fill these. Rendering them leaves ruled headings
  // with no body — the shape that reads as analysis while asserting nothing.
  //
  // Filtered at RENDER only. `report.sectionOrder` stays as persisted, and the
  // V2 `document.omissions` rows and the `semantic.adequacy: 'inadequate'` they
  // produce are left intact — those are the correct downstream consequence.
  // Dropping a section at BUILD time made completeness pass vacuously once
  // before (see `resolveSectionIds` in buildReportDocumentV1), which is exactly
  // the regression not to repeat.
  //
  // Studio preview keeps every section visible so template authors can still
  // see and lay out a section this particular site happens not to evidence.
  const omitted = studioPreview ? new Set<string>() : hiddenReportSectionIds(effectiveReport);
  const order = omitted.size > 0
    ? persistedOrder.filter(id => !omitted.has(id))
    : persistedOrder;
  // William QA BUG #15 — Re-enabled to pass the C5 Option-C rubric.
  // Computed over the FILTERED order so part dividers cannot land on, or
  // orphan themselves ahead of, a section that is no longer rendered.
  const partBoundaries = computePartDividerBoundaries(order);
  const labels = new Map(sectionsForType.map(s => [s.sectionId, s.defaultLabel]));
  const labelForSection = (id: string): string => {
    if (isCustomBriefQuestionSectionId(id)) {
      const qid = parseCustomBriefQuestionSectionId(id);
      if (!qid) return labels.get(id) ?? id;
      const answer = (effectiveReport.customBriefAnswers ?? []).find(a => a.questionId === qid);
      if (answer?.question?.trim()) {
        const qNum = resolveCustomBriefQuestionNumber(
          effectiveReport.customBriefAnswers,
          qid,
          order,
        );
        return customBriefQuestionHeading(answer.question, qNum);
      }
    }
    return labels.get(id) ?? id;
  };
  const anchorIdForSection = (id: string): string => {
    if (isCustomBriefQuestionSectionId(id)) {
      const qid = parseCustomBriefQuestionSectionId(id);
      if (!qid) return id;
      const answer = (effectiveReport.customBriefAnswers ?? []).find(a => a.questionId === qid);
      if (answer?.questionSlug?.trim()) return answer.questionSlug.trim();
    }
    return id;
  };
  const seen = new Set<string>();
  const PSR_ONLY_IDS = new Set([
    'executive', 'stakeholderMap', 'perBtmSentiment', 'themeDeepDives',
    'trendAnalysis', 'comparableSiteBenchmark', 'mitigationPlaybook',
  ]);
  // COMBINES — these ids are FOLDED INTO a surviving feasibility section and
  // must NOT render their own standalone mount on a FEASIBILITY report:
  //   - connectivityStudy   → folded into transportStudy ("Logistics &
  //                           Connectivity"); TransportStudySection renders the
  //                           connectivity body as a sub-block.
  //   - capacityCreditDeepDive → folded into gridStudy ("Grid Power Study");
  //                           GridStudySection renders the ELCC body as a
  //                           sub-block.
  //   - pathway_comparison  → folded into lcoeChart ("LCOE Cost Comparison");
  //                           LcoeChartSection (inline in SiteRagReportView)
  //                           renders the pathway body as a sub-block.
  // All three are SHARED registry ids (connectivityStudy is feasibility-only but
  // kept registered for archived back-compat; capacityCreditDeepDive is shared
  // with power-availability; pathway_comparison is shared with
  // test-fit/financial/executive-summary), so the suppression is keyed on
  // reportType — a NEW feasibility report no longer lists them in
  // REPORT_SECTIONS, but an ARCHIVED feasibility report's persisted sectionOrder
  // still does, and without this guard the router would double-render them
  // alongside the fold. (lcoeChart itself is feasibility-inline, not a router
  // section, so the pathway fold happens in SiteRagReportView's LcoeChartSection
  // mount; the router only needs to SUPPRESS the standalone pathway here.)
  const FEASIBILITY_FOLDED_IDS = new Set([
    'connectivityStudy', 'capacityCreditDeepDive', 'pathway_comparison',
  ]);
  return (
    <>
      {order.map(id => {
        if (seen.has(id)) return null;
        if (PSR_ONLY_IDS.has(id) && effectiveReport.reportType !== 'public-sentiment') return null;
        if (FEASIBILITY_FOLDED_IDS.has(id) && effectiveReport.reportType === 'feasibility') return null;
        // v1.2.2 — public-sentiment routes its exec content through the
        // `executive` id; when the cover already carries the exec-summary-
        // equivalent, suppress this body section so the prose isn't duplicated
        // (the parent drops the matching TOC entry). Gated on
        // `suppressExecutiveSection` (= coverHasExecSummaryContent) so a thin
        // report with a non-carrying cover keeps its body Executive section.
        if (id === 'executive' && suppressExecutiveSection) return null;
        let Component = NEW_SECTION_REGISTRY[id];
        // P3 — public-sentiment back-compat. The sentiment evidence inventory now
        // has its own `sentimentAppendix` id (rendered via the registry above), so
        // the standard `appendix` id keeps the references/methodology appendix
        // (rendered inline by ReferencesSection). For ARCHIVED public-sentiment
        // reports whose persisted sectionOrder still carries the OLD single
        // `appendix` (and NOT the new `sentimentAppendix`), remap `appendix` to the
        // sentiment inventory so those reports don't lose the inventory content.
        if (
          id === 'appendix'
          && effectiveReport.reportType === 'public-sentiment'
          && !order.includes('sentimentAppendix')
        ) {
          Component = SentimentAppendixSection;
        }
        if (id === 'actionPlan' && effectiveReport.reportType === 'executive-summary') {
          Component = ExecutiveSummaryActionPlanSection;
        }
        if (id === 'actionPlan' && effectiveReport.reportType === 'supply-chain-risk') {
          Component = SupplyChainActionPlanSection;
        }
        // #34 P2 STAGE B — feasibility's RICH actionPlan (phased EditorialTimeline
        // + numbered EditorialRecommendations) must NOT route through the generic
        // PowerAvailability `ActionPlanSection` stub. `actionPlan` is a shared id
        // (feasibility / power / water / environmental), so the override is keyed
        // on reportType, never the bare id.
        if (id === 'actionPlan' && effectiveReport.reportType === 'feasibility') {
          Component = FeasibilityActionPlanSection;
        }
        if (!Component && isCustomBriefQuestionSectionId(id)) {
          Component = CustomBriefQuestionSection;
        }
        if (!Component) return null;
        // HR-QA bare-heading guard (see hook above) — when the pathway
        // comparison would self-omit, drop the whole shell so no orphan
        // heading renders. Studio preview keeps it for placement.
        if (id === 'pathway_comparison' && !studioPreview) {
          const snapshot = effectiveReport.pathwayComparisonAtGen;
          if (!snapshot || (snapshot.pathways?.length ?? 0) < 2) return null;
        }
        // Capacity-Credit (ELCC) bare-heading guard — mirror the body's broader
        // render predicate (CapacityCreditDeepDiveSection.hasData) so no empty
        // heading shell mounts when the section has no firm-capacity content. Keeps
        // the router in lockstep with the TOC, which numbers this section on the
        // SAME predicate (capacityCreditDeepDiveHasContent). On feasibility reports
        // capacityCreditDeepDive is already folded into gridStudy above, so this
        // only fires for power-availability.
        if (id === 'capacityCreditDeepDive' && !capacityCreditDeepDiveHasContent(effectiveReport)) {
          return null;
        }
        // Optional generation exhibit: a complete frozen exhibit renders; an
        // older generationImpactAtGen renders the component's explicit
        // regenerate/accountability state. With neither immutable snapshot,
        // omit both the body and its conditional TOC entry. Never inspect live
        // Site/Zustand layers here.
        if (
          id === 'generationLayoutExhibit'
          && !generationLayoutExhibitHasContent(effectiveReport as unknown as Record<string, unknown>)
        ) {
          return null;
        }
        // Same contract for the screened-configurations section: no real
        // comparison ⇒ no body and no TOC entry.
        if (
          id === 'generationScenarioComparison'
          && !generationScenarioComparisonHasContent(effectiveReport as unknown as Record<string, unknown>)
        ) {
          return null;
        }
        if (id === 'waterScreening' && !waterScreeningHasContent(effectiveReport)) {
          return null;
        }
        if (id === 'gridLedStandbyPlanning' && !gridLedStandbyPlanningReportHasContent(effectiveReport)) return null;
        seen.add(id);
        const label = labelForSection(id);
        const anchorId = anchorIdForSection(id);
        const pageCols = sectionPageColumnCount?.(id) ?? 1;
        // ROUND-3 DEFECT-A — stamp the page-flow classification so a
        // column-capable prose section in a 2-col preset resolves to
        // data-section-flow="flow" (its prose packs into the page columns) and
        // a chart/card/wide section resolves to "span". undefined ⇒ omitted
        // (single-column / legacy presets) → byte-stable.
        const flowMode = sectionFlowMode?.(id);
        // #34 P2 STAGE B — feasibility shell parity. Feasibility's sections moved
        // onto this router from inline blocks that used the theme-scaled heading
        // chrome (reportSectionHeadingClasses) and did NOT add `space-y-4` to the
        // section shell; riskAssessment + actionPlan additionally carried `mb-6`
        // on the heading. To keep the rendered DOM IDENTICAL to the inline
        // baseline (reportSurfaceParity oracle), feasibility reproduces those
        // exact classes here. Every OTHER report type keeps the canonical router
        // chrome (REPORT_SECTION_H2_CLASS + `space-y-4`) byte-for-byte.
        const isFeasibility = effectiveReport.reportType === 'feasibility';
        const feasibilityHeading = isFeasibility ? reportSectionHeadingClasses(theme) : null;
        const headingExtra = isFeasibility && (id === 'riskAssessment' || id === 'actionPlan')
          ? ' mb-6'
          : '';
        const h2Class = feasibilityHeading
          ? `${feasibilityHeading.h2Class}${headingExtra}`
          : REPORT_SECTION_H2_CLASS;
        const headingTextClass = feasibilityHeading
          ? feasibilityHeading.textClass
          : REPORT_SECTION_HEADING_TEXT_CLASS;
        // The lifted feasibility STUDY sections' inline shells did NOT carry
        // `space-y-4`; only pathway_comparison (and the non-feasibility router
        // sections) did. Drop `space-y-4` for the study set so the shell class
        // is identical to the inline baseline.
        const feasibilityNoSpaceY = isFeasibility && FEASIBILITY_NO_SPACE_Y_IDS.has(id);
        const shellExtraClassName = !shellPadding
          ? undefined
          : feasibilityNoSpaceY
            ? sectionChrome
            : `${sectionChrome} space-y-4`;
        // Part L2 — the editorial OPENER numeral, via the SHARED
        // EditorialHeroNumeral primitive (the same treatment the inline
        // feasibility sections use, so the opener is consistent everywhere).
        // Sourced from the TOC-aligned section number the parent supplies; an
        // out-of-range / absent number drops the numeral. Rendered ONLY when
        // editorialActive — OFF ⇒ nothing added, the section <h2>/content are
        // byte-identical to today.
        const sectionNumberAttrs = sectionNumberProps?.(id) ?? {};
        const editorialSectionNumber = Number.parseInt(
          (sectionNumberAttrs as { 'data-section-number'?: string })['data-section-number'] ?? '',
          10,
        );

        // The section heading node (EditableHeading when editing-capable, else
        // the plain label) — SHARED by both the classic shell and the editorial
        // shell so the TOC anchor / edit wiring are identical on both paths.
        const headingNode = onHeadingChange ? (
          <EditableHeading
            headingKey={id}
            defaultText={sectionDefaultHeading?.(id, label) ?? label}
            className={headingTextClass}
            isEditing={isEditing}
            overrides={headingOverrides ?? {}}
            onChange={onHeadingChange}
          />
        ) : (
          label
        );
        // WAVE 3 — the GENERIC monograph prose path for ALL report types. When a
        // GENERATED editorial layer exists for this section, emit the flowing
        // EditorialProseBody (drop-cap + pull-quote, grounding-validated re-voice)
        // ABOVE the section's structured component body — so every routed section
        // of every type reads prose-led, not as a bare taxonomy-name header. The
        // two feasibility study sections that mount their OWN prose body (and
        // suppress their labelled blocks in tandem) are excluded here so the prose
        // is never doubled. No layer ⇒ nothing added (byte-identical; the
        // reportSurfaceParity fixtures carry no editorialNarrative, so they always
        // take this no-op path).
        const proseBodyGeneric =
          hasGeneratedEditorialLayer(effectiveReport, id)
          && !PROSE_BODY_SELF_MOUNT_IDS.has(id) ? (
            <EditorialProseBody report={effectiveReport} sectionId={id} theme={theme} />
          ) : null;

        // The section body — provenance strip, admin meta-leak chip, the section
        // component — SHARED verbatim between both shells (zero data loss; every
        // table / chart / KPI / finding / citation renders identically).
        const sectionBody = (
          <>
            <ProvenanceStrip report={effectiveReport} sectionId={id} />
            {isAdmin && onSectionRegen && (
              <MetaLeakChip
                report={effectiveReport}
                sectionId={id}
                sectionLabel={label}
                isAdmin={isAdmin}
                onRegenerate={onSectionRegen}
              />
            )}
            {proseBodyGeneric}
            <RouterSectionChromeProvider value={routerChrome}>
              <SectionIdProvider value={id}>
                <Component report={effectiveReport} label={label} sectionId={id} />
              </SectionIdProvider>
            </RouterSectionChromeProvider>
          </>
        );

        const sharedShellProps = {
          id: anchorId,
          'data-section': id,
          'data-section-id': id,
          'data-section-columns': String(pageCols),
          ...(flowMode ? { 'data-section-flow': flowMode } : {}),
          ...sectionNumberAttrs,
          style: sectionStyle(id),
          theme,
          omitSectionSidebar,
          shellPadding,
        } as const;

        // ── N1.5C (Editorial Report Redesign v2.0) — UNIFORM editorial engine
        // across ALL report types. The ONE EditorialSectionShell selects its mode
        // via `editorialActive`:
        //   • ON  → the full magazine opener (serif hero-numeral + numbered head +
        //           italic standfirst) REPLACES the bare ruled <h2>, and the shell
        //           stamps `data-editorial-section` so the report-root
        //           `[data-editorial]` drop-cap / pull-quote / `data-flow` CSS hooks
        //           fire over the body. The standfirst is the feasibility-specific
        //           lede for feasibility (resolveFeasibilityStandfirst) and absent
        //           (no lede) for every other type — never fabricated.
        //   • OFF → byte-identical to the retired ClassicSectionShell: the shell
        //           wraps the caller's own <h2> + body, no opener, no marker.
        // The body is rendered verbatim in BOTH modes — zero data loss, no citation
        // reindex. N1.5C lifts the prior `editorialActive && isFeasibility` gate to
        // ALL 11 types; the flag-OFF DOM is unchanged for every type.
        // WAVE 3 — opener THESIS line. When a GENERATED editorial layer is
        // present for this section the standfirst becomes the model thesis
        // (conclusion-first, decisive numbers, [Ref N] — grounding-validated).
        // Absent (flag OFF / legacy / section rejected) it is byte-identical to
        // today: the feasibility-only resolveFeasibilityStandfirst lede, or
        // undefined for every other type. The fallback path is NEVER changed.
        const editorialThesis = hasGeneratedEditorialLayer(effectiveReport, id)
          ? resolveEditorialLayer(effectiveReport, id).editorialThesis
          : undefined;
        const editorialStandfirstText = editorialThesis
          ?? (isFeasibility ? resolveFeasibilityStandfirst(effectiveReport, id) : undefined);
        // CITE-PILL-1 (2026-07-03, William) — standfirst ledes carried literal
        // "[Ref N]" text (they interpolated the raw string). Route through the
        // SAME claims walker the body uses so citations render as hyperlinked
        // superscript pills on every surface that shows a lede.
        const editorialStandfirst = typeof editorialStandfirstText === 'string'
          ? walkClaimsText(editorialStandfirstText, effectiveReport.references, effectiveReport.claims)
          : editorialStandfirstText;

        // William QA BUG #15 — The dark Part-divider sheet was restored to fulfill the C5 rubric.
        // It renders BEFORE the section when its id matches a computed part boundary.
        // AI LAYOUT PASS (emphasis) — the plan can ADD a part-divider / request a
        // hero opener for this section as a FLOOR. partDivider is OR'd with the
        // engine's computed boundary set (additive — it never removes one) and,
        // like the engine boundary, stays gated on `editorialActive`. ABSENT plan
        // ⇒ both lookups are `false`, so `isPartBoundary` + the hero opener are
        // byte-identical to today.
        const planPartDivider =
          report.layoutPlan?.sections?.[id]?.emphasis?.partDivider === true;
        const planHeroEmphasis =
          report.layoutPlan?.sections?.[id]?.emphasis?.hero === true;
        const isPartBoundary = editorialActive && (partBoundaries.has(id) || planPartDivider);
        const partDividerNode = isPartBoundary ? (
          <EditorialPartDivider
            key={`divider-${id}`}
            theme={theme}
            part={EDITORIAL_PARTS[resolvePartForSection(id)]}
            tocEntries={buildPartTocEntries(resolvePartForSection(id), order, labelForSection, id)}
          />
        ) : null;

        return (
          <React.Fragment key={`sec-${id}`}>
          {partDividerNode}
          <EditorialSectionShell
            key={id}
            {...sharedShellProps}
            editorialActive={editorialActive}
            sectionNumber={editorialSectionNumber}
            heading={headingNode}
            standfirst={editorialStandfirst}
            standfirstIsThesis={Boolean(editorialThesis)}
            heroEmphasis={planHeroEmphasis}
            extraClassName={shellExtraClassName}
          >
            {/* Flag-OFF: the caller-owned <h2> rides inside the shell exactly as
                the retired ClassicSectionShell rendered it (byte-stable). Flag-ON:
                the shell owns the opener <h2>, so this one is suppressed. */}
            {!editorialActive && (
              <h2
                className={h2Class}
                style={reportSectionHeadingStyleScaled(theme)}
                data-heading="true"
              >
                {headingNode}
              </h2>
            )}
            {sectionBody}
          </EditorialSectionShell>
          </React.Fragment>
        );
      })}
    </>
  );
});
NewTypeSectionsRouter.displayName = 'NewTypeSectionsRouter';
